import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const org = data.organizations?.find((o) => o.id === data.active_org_id) || data.org;
      if (org?.currency) localStorage.setItem("avocado_currency", org.currency);
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("avocado_token")) return setUser(false);
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    let data;
    for (let attempt = 0; ; attempt++) {
      try { ({ data } = await api.post("/auth/login", { email, password })); break; }
      catch (e) {
        if (!e.response && attempt < 3) { await new Promise((r) => setTimeout(r, 1500 * (attempt + 1))); continue; }
        if (!e.response) throw new Error("Cannot reach the server. Please check your connection and try again.");
        throw e;
      }
    }
    localStorage.setItem("avocado_token", data.token);
    localStorage.removeItem("avocado_org");
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("avocado_token");
    localStorage.removeItem("avocado_org");
    setUser(false);
  };

  const switchOrg = async (orgId) => {
    localStorage.setItem("avocado_org", orgId);
    await refresh();
  };

  const can = (key, action = "view") => {
    const code = user?.permissions?.[key] || "-";
    if (code === "-") return false;
    const c = code.split(":")[0];
    if (action === "view") return true;
    if (action === "approve") return c.includes("A") || c.includes("F");
    return c.includes("F");
  };
  const scope = (key) => (user?.permissions?.[key] || "").split(":")[1] || null;
  const code = (key) => (user?.permissions?.[key] || "-").split(":")[0];

  return <AuthContext.Provider value={{ user, login, logout, refresh, switchOrg, can, scope, code }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
